import { Box, Skeleton, Typography } from "@mui/material";
import type { JSX } from "react";

import { EMPTY } from "../../../lib/format.js";

/**
 * The three money figures, set as a ledger rather than as cards.
 *
 * WHY NOT THREE MORE STAT CARDS. Six identical tiles is what this page was,
 * and a grid of equal boxes tells a reader that six things matter equally.
 * They do not: what a reseller was invoiced, what is still owed and what is
 * late are ONE story read left to right, and the last of the three is the
 * only one that ever needs acting on. Hairlines instead of boxes make it a
 * single object; the counts move to their own lighter row below.
 *
 * The figures sit on the theme's tabular numerals (`fontFeatureSettings:
 * "tnum"`, set globally in theme.ts), so the three columns line up digit for
 * digit instead of drifting with the glyph widths.
 */

export interface LedgerFigure {
  label: string;
  /** Already formatted by `lib/format.ts` — an unavailable figure is an em
   * dash by the time it reaches here, never a zero. */
  value: string;
  /** The line under the figure: what it is made of. Null renders nothing
   * rather than an empty row, so the baseline stays even. */
  sub: string | null;
  /** Draws the figure in the error colour. Reserved for a real, non-zero
   * overdue balance — a red "—" would read as a problem where there is only
   * a missing number. */
  alarming?: boolean;
  testId: string;
}

export function Ledger({
  figures,
  pending = false,
}: {
  figures: readonly LedgerFigure[];
  /** While the request is in flight the figure is a skeleton, NOT an em
   * dash. The dash means "the CRM could not supply this"; showing it for
   * half a second on every page load would cry wolf. */
  pending?: boolean;
}): JSX.Element {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: `repeat(${figures.length}, 1fr)` },
      }}
    >
      {figures.map((figure, i) => (
        <Box
          key={figure.label}
          data-testid={figure.testId}
          sx={{
            // The rule falls BETWEEN columns on a wide screen and between
            // rows on a narrow one — same separator, following the flow.
            borderTop: { xs: i > 0 ? 1 : 0, sm: 0 },
            borderLeft: { xs: 0, sm: i > 0 ? 1 : 0 },
            borderColor: { xs: "divider", sm: "divider" },
            pt: { xs: i > 0 ? 2 : 0, sm: 0 },
            mt: { xs: i > 0 ? 2 : 0, sm: 0 },
            pl: { xs: 0, sm: i > 0 ? 3 : 0 },
            pr: { sm: 3 },
          }}
        >
          <Typography
            component="p"
            sx={{
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            {figure.label}
          </Typography>
          {pending ? (
            <Skeleton variant="text" width="60%" sx={{ mt: 0.5, fontSize: "1.9rem" }} />
          ) : (
          <Typography
            component="p"
            sx={{
              mt: 0.5,
              fontFamily: (t) => t.typography.h1.fontFamily,
              fontSize: "clamp(1.4rem, 1.05rem + 1.1vw, 1.9rem)",
              fontWeight: 600,
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
              color:
                figure.value === EMPTY
                  ? "text.disabled"
                  : figure.alarming
                    ? "error.main"
                    : "text.primary",
            }}
          >
            {figure.value}
          </Typography>
          )}
          {!pending && figure.sub && (
            <Typography component="p" sx={{ mt: 0.25, fontSize: 12, color: "text.secondary" }}>
              {figure.sub}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}
